import axios from 'axios';
import { API_BASE_URL } from '../config';
import { buildLoginRedirectUrl } from './redirectUtils';

// Token Service for managing access token
class TokenService {
  static getToken() {
    return localStorage.getItem('accesstoken');
  }

  static setToken(token) {
    localStorage.setItem('accesstoken', token);
  }

  static removeToken() {
    localStorage.removeItem('accesstoken');
  }
}

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const refreshAccessToken = async () => {
  try {
    // console.log('Refreshing access token...');
    const response = await axios.post(`${axiosInstance.defaults.baseURL}auth/refresh-token`, {}, { withCredentials: true });
    const newAccessToken = response.data.accessToken;
    // console.log('Received a new access token from server:', newAccessToken);
    if (newAccessToken) {
      TokenService.setToken(newAccessToken);
    }
    return newAccessToken;
  } catch (error) {
    console.error('Unable to refresh token:', error);
    return null;
  }
};

// Request interceptor for adding the access token to the request header
axiosInstance.interceptors.request.use((request) => {
  const accessToken = TokenService.getToken();
  if (accessToken) {
    request.headers['x-access-token'] = accessToken;
  }

  // Browsers LOVE to cache stuff, especially GET requests, to circumvent this, we can append a timestamp
  const url = new URL(request.url, request.baseURL);
  url.searchParams.set('_t', Date.now());
  request.url = url.toString().replace(url.origin, '');

  return request;
}, (error) => Promise.reject(error));

// Response interceptor for handling token refresh and other errors
axiosInstance.interceptors.response.use((response) => {
  // Check and update token if present in the response
  const newToken = response.headers['x-access-token'];
  if (newToken) {
    TokenService.setToken(newToken);
  }

  const redirect = response.headers['x-redirect'];
  const onPublicAuthRoute = window.location.pathname.startsWith('/password-reset')
    || window.location.pathname.startsWith('/onboarding/email');
  if (redirect && window.location.pathname !== redirect && !onPublicAuthRoute) {
    window.location.href = redirect;
  }

  return response;
}, async (error) => {
  const originalRequest = error.config;

  const requestPath = (originalRequest?.url || '').split('?')[0];
  if (requestPath.startsWith('/auth/') || requestPath.startsWith('auth/')) {
    return Promise.reject(error);
  }

  if (error.response && error.response.status === 401 && !originalRequest._retry) {
    if (isRefreshing) {
      return new Promise(function(resolve, reject) {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers['x-access-token'] = token;
        return axiosInstance(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newAccessToken = await refreshAccessToken();

      if (newAccessToken) {
        originalRequest.headers['x-access-token'] = newAccessToken;
        processQueue(null, newAccessToken);
        return axiosInstance(originalRequest);
      } else {
        processQueue(new Error('Token refresh failed'), null);
        // Token refresh failed — clear session so PrivateWrapper stops rendering protected routes
        console.error('Token refresh failed, redirecting to login...');
        TokenService.removeToken();
        localStorage.removeItem('user');
        window.dispatchEvent(new Event('auth:session-expired'));

        if (window.location.pathname !== '/login') {
          window.location.href = buildLoginRedirectUrl(window.location.pathname, window.location.search, window.location.hash);
        }
      }
    } catch (error) {
      processQueue(error, null);
    } finally {
      isRefreshing = false;
    }
  }

  return Promise.reject(error);
});

export default axiosInstance;
